import { useContext, useEffect, useMemo, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";

import { Tabs } from "antd";
import { RootState } from "store/rootReducer";
import styled from "styled-components";

import { updateGeomBinGroupOrder, updateGroupOrder } from "api/data";

import { IGroupBy, IGroupByListColumn } from "models";
import { EntityKind } from "models/entityKind";

import { IconSpinner } from "components/icons";

import { PresetDropdownContext } from "../filter/focus/PresetDropdown";
import FieldGroup, { GroupType } from "./FieldGroup";
import { useGroupByStore } from "./GroupByStoreProvider";
import {
  selectCustomFieldsEditToggle,
  selectEntityKind,
  selectIsFiltering,
  selectSelectedEndDate,
  selectSelectedStartDate,
  selectSelectedTab
} from "./groupBySelectors";
import { groupFields, groupFieldsByDateRange } from "./helpers";
import { useColumns, useFieldsWithLabels, useSelectedCategory } from "./hooks";
import { useGeomBinGroupOrderQuery } from "./queries/useGeomBinGroupOrderQuery";
import { useGroupOrderQuery } from "./queries/useGroupOrderQuery";

interface FieldSelectionData {
  field: IGroupByListColumn;
  onChange?: (
    value: IGroupBy | IGroupByListColumn | Record<string, unknown>,
    isForecastToggleOn?: boolean,
    selectedForecastFolder?: string,
    selectedForecastFolderName?: string,
    selectedPdenSource?: number
  ) => void;
  isForecastToggleOn?: boolean;
  selectedForecastFolder?: string;
  selectedForecastFolderName?: string;
  selectedPdenSource?: number;
}

interface FieldListProps {
  active?: boolean;
  focusedIndex?: Maybe<number>;
  tabsActive?: boolean;
  tabsFocusedIndex?: Maybe<number>;
  isDisablingManagement?: boolean;
  onItemRef?: (index: number, el: Maybe<HTMLElement>) => void;
  onTabItemRef?: (index: number, el: Maybe<HTMLElement>) => void;
  onFieldClick?: (fieldData: FieldSelectionData) => void;
  onFieldDoubleClick?: (fieldData: FieldSelectionData) => void;
}

const FieldList = ({
  active = false,
  focusedIndex = null,
  tabsActive = false,
  tabsFocusedIndex = null,
  isDisablingManagement = false,
  onItemRef,
  onTabItemRef,
  onFieldClick,
  onFieldDoubleClick
}: FieldListProps) => {
  const shortcutsEnabled = useContext(PresetDropdownContext);

  const customFieldsEditToggle = useGroupByStore(selectCustomFieldsEditToggle);
  const selectedTab = useGroupByStore(selectSelectedTab);
  const isFiltering = useGroupByStore(selectIsFiltering);
  const entityKind = useGroupByStore(selectEntityKind);
  const setSelectedTab = useGroupByStore((s) => s.setSelectedTab);
  const setCheckedFields = useGroupByStore((s) => s.setCheckedFields);
  const {
    isOrgDefinedColumn,
    isUserDefinedColumn,
    isOrgGeoDefinedColumn,
    isProjectDefinedColumn,
    isOrgGeoSimsDefinedColumn,
    hasPerformaceFields
  } = useSelectedCategory();
  const selectedStartDate = useGroupByStore(selectSelectedStartDate);
  const selectedEndDate = useGroupByStore(selectSelectedEndDate);

  const columns = useColumns();
  const labelledColumns = useFieldsWithLabels(columns);
  const groupedColumns = groupFields(labelledColumns || columns);

  const [fieldVisible, setFieldVisible] = useState(true);

  const [fieldGroups, setFieldGroups] = useState([]);
  const [binGroups, setBinGroups] = useState([]);
  const [draggingId, setDraggingId] = useState(null);
  const projectId = useSelector(
    (state: RootState) => state.groupBy.selectedProjectIdForColumns
  );
  const isRefreshingCategories = useSelector((state: RootState) =>
    entityKind === EntityKind.Facility
      ? state.groupBy.isRefreshingCategoriesFacility
      : state.groupBy.isRefreshingCategoriesWell
  );
  const isCustomColumn = useMemo(() => {
    return isOrgDefinedColumn || isUserDefinedColumn || isProjectDefinedColumn;
  }, [isOrgDefinedColumn, isUserDefinedColumn, isProjectDefinedColumn]);

  const groupOrderQuery = useGroupOrderQuery({
    isOrg: isOrgDefinedColumn,
    projectId
  });

  const geomBinGroupOrderQuery = useGeomBinGroupOrderQuery({
    isOrg: isOrgDefinedColumn,
    projectId
  });

  useEffect(() => {
    let isCurrent = true;
    setFieldVisible(true);
    setFieldGroups([]);
    setBinGroups([]);
    if (
      groupedColumns.size === 0 ||
      groupOrderQuery.isLoading ||
      geomBinGroupOrderQuery.isLoading
    ) {
      return;
    }

    const groupedBins = Array.from(groupedColumns).reduce((acc, [key, list]) => {
      const geomFields = list
        .filter((l) => l.fieldType === "geom")
        .sort(
          (a, b) =>
            (a.withinSubgroupOrder ?? Infinity) - (b.withinSubgroupOrder ?? Infinity)
        );
      if (geomFields.length > 0) {
        acc.push({ key, geomFields });
      }
      return acc;
    }, []);

    // Unlike the per-category orderings guarded below, withinSubgroupOrder is
    // the browse order, so search keeps it.
    const groupedFields = Array.from(groupedColumns).reduce((acc, [key, list]) => {
      const fields = list
        .filter((l) => l.fieldType !== "geom")
        .sort(
          (a, b) =>
            (a.withinSubgroupOrder ?? Infinity) - (b.withinSubgroupOrder ?? Infinity)
        );
      if (fields.length > 0) {
        acc.push({ key, fields });
      }
      return acc;
    }, []);

    // Search results span every category, so the selected category's ordering
    // and date-range grouping must not apply.
    const setOrder = () => {
      if (isCustomColumn && !isFiltering) {
        if (groupOrderQuery.isSuccess) {
          const orderMap = new Map();
          groupOrderQuery.data.forEach((item) => orderMap.set(item.name, item.order));
          groupedFields.sort((a, b) => orderMap.get(a.key) - orderMap.get(b.key));
        }

        if (geomBinGroupOrderQuery.isSuccess) {
          const geomOrderMap = new Map();
          geomBinGroupOrderQuery.data.forEach((item) =>
            geomOrderMap.set(item.name, item.order)
          );
          groupedBins.sort((a, b) => geomOrderMap.get(a.key) - geomOrderMap.get(b.key));
        }
      }

      if (isCurrent) {
        const hasDateRange = selectedStartDate != null && selectedEndDate != null;
        const finalFieldGroups =
          hasPerformaceFields && !isCustomColumn && !isFiltering
            ? groupFieldsByDateRange(groupedFields, hasDateRange)
            : groupedFields;
        setFieldGroups(finalFieldGroups);
        setBinGroups(groupedBins);
      }
    };

    setOrder();
    return () => {
      isCurrent = false;
    };
  }, [
    labelledColumns,
    groupOrderQuery.isSuccess,
    geomBinGroupOrderQuery.isSuccess,
    geomBinGroupOrderQuery.isLoading,
    groupOrderQuery.isLoading,
    selectedStartDate,
    selectedEndDate,
    hasPerformaceFields,
    isCustomColumn,
    isFiltering
  ]);

  const queryClient = useQueryClient();

  const onDragEnd = (result) => {
    setDraggingId(null);
    if (!result.destination) {
      return;
    }
    const selectedGroup = fieldVisible ? fieldGroups : binGroups;
    const reorderedItems = JSON.parse(JSON.stringify(selectedGroup));
    const [movedItem] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, movedItem);
    reorderedItems.forEach((item, index) => {
      item.withinSubgroupOrder = index;
    });

    const updatedItems = reorderedItems.map((item, index) => ({
      id: item.key,
      order: index
    }));

    if (fieldVisible) {
      updateGroupOrder(updatedItems, isOrgDefinedColumn, projectId);
      setFieldGroups(reorderedItems);
      const cacheData = updatedItems.map((item) => ({
        name: item.id,
        order: item.order
      }));
      queryClient.setQueryData(["groupOrder", isOrgDefinedColumn], cacheData);
    } else {
      updateGeomBinGroupOrder(updatedItems, isOrgDefinedColumn, projectId);
      setBinGroups(reorderedItems);
      const cacheData = updatedItems.map((item) => ({
        name: item.id,
        order: item.order
      }));
      queryClient.setQueryData(
        ["geomBinGroupOrder", isOrgDefinedColumn, projectId],
        cacheData
      );
    }
  };

  const getDragItemStyle = (isDragging, draggableStyle) => ({
    userSelect: "none",
    height: isDragging ? "26px" : "auto",
    ...draggableStyle
  });

  const handleTabChanged = (tabKey) => {
    setFieldVisible(tabKey === "1");
    setSelectedTab(tabKey === "1" ? "fields" : "bins");
    setCheckedFields([]);
  };

  useEffect(() => {
    if (isCustomColumn) {
      setFieldVisible(true);
    }
  }, [isCustomColumn]);

  const onBeforeCapture = (beforeCapture) => {
    setDraggingId(beforeCapture.draggableId);
  };

  if (groupOrderQuery.isLoading || geomBinGroupOrderQuery.isLoading)
    return <IconSpinner role="status" aria-label="Loading field groups" />;

  return (
    <>
      {isCustomColumn && !isFiltering && (
        <TabWrapper key={isOrgDefinedColumn ? "1" : "2"}>
          <TabNavigationContainer>
            <TabNavigationButton
              ref={(el) => onTabItemRef && onTabItemRef(0, el)}
              className={tabsActive && tabsFocusedIndex === 0 ? "keyboard-focused" : ""}
              onClick={() => handleTabChanged("1")}
            />
            {entityKind !== EntityKind.Facility && (
              <TabNavigationButton
                ref={(el) => onTabItemRef && onTabItemRef(1, el)}
                className={tabsActive && tabsFocusedIndex === 1 ? "keyboard-focused" : ""}
                onClick={() => handleTabChanged("2")}
              />
            )}
          </TabNavigationContainer>
          <Tabs
            activeKey={selectedTab == "fields" ? "1" : "2"}
            items={[
              {
                key: "1",
                label: "Fields",
                children: <></>
              },
              ...(entityKind !== EntityKind.Facility
                ? [
                    {
                      key: "2",
                      label: "Bins",
                      children: <></>
                    }
                  ]
                : [])
            ]}
            onChange={(item) => handleTabChanged(item)}
          />
          {isRefreshingCategories && (
            <LoadingSpinner>
              <IconSpinner role="status" aria-label="Refreshing field categories" />
            </LoadingSpinner>
          )}
        </TabWrapper>
      )}
      {isCustomColumn && (
        <DragDropContext onDragEnd={onDragEnd} onBeforeCapture={onBeforeCapture}>
          <Droppable droppableId="groups" direction="vertical" type="group">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {selectedTab == "fields" || isFiltering
                  ? fieldGroups.map((group, index) => {
                      // Calculate the starting index for this group (includes group header + fields from previous groups)
                      const fieldStartIndex = fieldGroups
                        .slice(0, index)
                        .reduce((acc, prevGroup) => acc + 1 + prevGroup.fields.length, 0); // +1 for each group header

                      return (
                        <Draggable
                          key={group.key}
                          draggableId={group.key}
                          index={index}
                          isDragDisabled={isFiltering}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={getDragItemStyle(
                                snapshot.isDragging || group.key === draggingId,
                                provided.draggableProps.style
                              )}>
                              <FieldGroup
                                dragHandleProps={provided.dragHandleProps}
                                key={group.key}
                                groupName={group.key}
                                fields={group.fields}
                                groups={fieldGroups}
                                isDragging={snapshot.isDragging}
                                shortcutsEnabled={shortcutsEnabled}
                                groupType={GroupType.FieldGroup}
                                active={active}
                                focusedIndex={focusedIndex}
                                fieldStartIndex={fieldStartIndex}
                                projectId={projectId}
                                isDisablingManagement={isDisablingManagement}
                                onItemRef={onItemRef}
                                onFieldClick={onFieldClick}
                                onFieldDoubleClick={onFieldDoubleClick}
                              />
                            </div>
                          )}
                        </Draggable>
                      );
                    })
                  : binGroups.map((group, index) => {
                      // Calculate the starting index for this group (includes group header + fields from previous groups)
                      const fieldStartIndex = binGroups
                        .slice(0, index)
                        .reduce(
                          (acc, prevGroup) => acc + 1 + prevGroup.geomFields.length,
                          0
                        ); // +1 for each group header

                      return (
                        <Draggable key={group.key} draggableId={group.key} index={index}>
                          {(provided, snapshot) => (
                            <div ref={provided.innerRef} {...provided.draggableProps}>
                              <FieldGroup
                                dragHandleProps={
                                  customFieldsEditToggle ? provided.dragHandleProps : null
                                }
                                key={group.key}
                                groupName={group.key}
                                fields={group.geomFields}
                                groups={fieldGroups}
                                isDragging={snapshot.isDragging}
                                shortcutsEnabled={shortcutsEnabled}
                                groupType={GroupType.BinGroup}
                                active={active}
                                focusedIndex={focusedIndex}
                                fieldStartIndex={fieldStartIndex}
                                projectId={projectId}
                                isDisablingManagement={isDisablingManagement}
                                onItemRef={onItemRef}
                                onFieldClick={onFieldClick}
                                onFieldDoubleClick={onFieldDoubleClick}
                              />
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
      {!isCustomColumn &&
        !isOrgGeoDefinedColumn &&
        !isOrgGeoSimsDefinedColumn &&
        fieldGroups.map((group, index) => {
          const fieldStartIndex = fieldGroups
            .slice(0, index)
            .reduce((acc, prevGroup) => acc + 1 + prevGroup.fields.length, 0);

          const hasDateRange = selectedStartDate != null && selectedEndDate != null;

          return (
            <FieldGroup
              key={group.key}
              groupName={group.key}
              fields={group.fields}
              groups={fieldGroups}
              dragHandleProps={null}
              isDragging={false}
              shortcutsEnabled={shortcutsEnabled}
              active={active}
              focusedIndex={focusedIndex}
              fieldStartIndex={fieldStartIndex}
              projectId={projectId}
              isPerformanceDateRange={hasPerformaceFields && hasDateRange && !isFiltering}
              isDisablingManagement={isDisablingManagement}
              onItemRef={onItemRef}
              onFieldClick={onFieldClick}
              onFieldDoubleClick={onFieldDoubleClick}
            />
          );
        })}
    </>
  );
};

export default FieldList;

const TabWrapper = styled.div`
  padding: 0 var(--space-4);
  // gap: 5px;
  position: relative;
`;

const LoadingSpinner = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  z-index: 2;

  svg {
    width: 16px;
    height: 16px;
    color: var(--color-primary);
  }
`;

const TabNavigationContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 46px;
  display: flex;
  z-index: 1;
  pointer-events: none;
  padding: 4px 4px 0 4px;
`;

const TabNavigationButton = styled.button.attrs({ type: "button" })`
  height: 40px;
  min-width: 80px;
  background: transparent;
  border: none;
  padding: 10px;
  margin: 0 0 0 0;
  pointer-events: auto;
  cursor: pointer;
  border-radius: 6px;
  position: relative;

  &.keyboard-focused {
    outline: 2px solid var(--color-primary);
    outline-offset: -2px;
    z-index: 2;
  }

  &:focus {
    outline: none;
  }

  /* Adjust for when there are exactly 2 tabs */
  &:first-child {
    flex: 0 0 auto;
    width: auto;
    min-width: 65px;
  }

  &:last-child {
    flex: 0 0 auto;
    width: auto;
    min-width: 65px; /* "Bins" is shorter than "Fields" */
  }
`;
